/**
 * Money is always an integer number of minor units (cents for USD) plus an
 * explicit currency. Never a float. All arithmetic on money goes through here.
 */
export type CurrencyCode = 'USD'

export const SUPPORTED_CURRENCIES: readonly CurrencyCode[] = ['USD'] as const

export interface Money {
  readonly amountMinor: number
  readonly currency: CurrencyCode
}

export class MoneyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MoneyError'
  }
}

export function money(amountMinor: number, currency: CurrencyCode = 'USD'): Money {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new MoneyError(`amountMinor must be a safe integer, got ${amountMinor}`)
  }
  if (!SUPPORTED_CURRENCIES.includes(currency)) {
    throw new MoneyError(`unsupported currency ${currency}`)
  }
  return { amountMinor, currency }
}

export function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new MoneyError(`currency mismatch: ${a.currency} vs ${b.currency}`)
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return money(a.amountMinor + b.amountMinor, a.currency)
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return money(a.amountMinor - b.amountMinor, a.currency)
}

/** Scale by an integer count (quantity × unit price). Fractional factors are a MoneyError. */
export function multiply(m: Money, factor: number): Money {
  if (!Number.isSafeInteger(factor)) {
    throw new MoneyError(`factor must be a safe integer, got ${factor}`)
  }
  return money(m.amountMinor * factor, m.currency)
}

export function sum(items: readonly Money[], currency: CurrencyCode = 'USD'): Money {
  return items.reduce((acc, m) => add(acc, m), money(0, currency))
}

export function isZero(m: Money): boolean {
  return m.amountMinor === 0
}

export function isNegative(m: Money): boolean {
  return m.amountMinor < 0
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b)
  if (a.amountMinor < b.amountMinor) return -1
  if (a.amountMinor > b.amountMinor) return 1
  return 0
}

/**
 * Parse a user-entered decimal string such as "12,345.60" or "$1,000".
 * Rejects more precision than the currency supports rather than rounding.
 */
export function parseDecimal(input: string, currency: CurrencyCode = 'USD'): Money {
  const cleaned = input.trim().replace(/[$,\s]/g, '')
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned)
  if (!match) {
    throw new MoneyError(`cannot parse money from "${input}"`)
  }
  const [, sign, whole = '0', fraction = ''] = match
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return money(sign ? -minor : minor, currency)
}

export function formatMoney(m: Money, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: m.currency }).format(m.amountMinor / 100)
}
